
import { useCallback } from "react";
import token_icon from "assets/token.svg";
import { Form } from "formik";
import { useMappedState } from "redux-react-hook";
import { useGetTokenPricesQuery } from "services/api/tokenApi";
import styled from "styled-components";
import { ICharacter } from "types/characters";
import Spinner from 'ui-kit/Spinner';

import magic_stick_icon from '../../../../assets/magic_stick_icon.svg';
import {
  backgroundOptions,
  clothingLevelOptions,
  IPhotoGeneratorFormValues,
  poseOptions,
  positionOptions
} from '../../form';

interface IGenerationStepProps {
  character: ICharacter;
  values: IPhotoGeneratorFormValues;
  setFieldValue: (field: string, value: string) => void;
  isGenerationLoading: boolean;
  onOpenSubscriptionModal: () => void;
  handleSubmit: (e?: React.FormEvent<HTMLFormElement>) => void
  openBuyTokensModal: () => void;
}

const GenerationStep: React.FC<IGenerationStepProps> = (props) => {
  const { character, values, setFieldValue, isGenerationLoading = false, onOpenSubscriptionModal, handleSubmit, openBuyTokensModal } = props;

  const { data: { costs: { photoGenerationCost } = {} } = {}, isLoading: isTokenPriceLoading } = useGetTokenPricesQuery();

  const isLoading = isGenerationLoading || isTokenPriceLoading;

  const { currentUser, hasSubscription } = useMappedState(
    useCallback(state => {
      const currentUser = state.users.usersMap.get(state.users.currentUserId);
      const hasSubscription = !!currentUser?.hasSubscription;
      return {
        currentUser: state.users.usersMap.get(state.users.currentUserId),
        hasSubscription: hasSubscription
      };
    }, [])
  );

  const tokenBalance = currentUser?.tokenBalance ?? 0;
  const isTokenBalanceLessThanCost = photoGenerationCost && photoGenerationCost > tokenBalance;

  const handleGenerateButtonClick = () => {
    if (isTokenBalanceLessThanCost && !hasSubscription) {
      onOpenSubscriptionModal();
      return;
    }
    if (isTokenBalanceLessThanCost && hasSubscription) {
      openBuyTokensModal();
      return;
    }
    handleSubmit();
  };

  return (
    <Container isLoading={isLoading}>
      <CharacterInfo>
        <CharacterImage src={character?.avatar} alt={character?.name} />
        <CharacterName>{character.name}</CharacterName>
      </CharacterInfo>
      <Form>
        <FormWrapper>
          <Section>
            <SectionTitle>Pose</SectionTitle>
            <OptionGroup>
              {poseOptions.map(({ label, value }) => (
                <OptionButton
                  key={value}
                  type="button"
                  selected={values.pose === value}
                  onClick={() => setFieldValue("pose", value)}
                  disabled={isLoading}
                >
                  {label}
                </OptionButton>
              ))}
            </OptionGroup>
          </Section>

          <Section>
            <SectionTitle>Position</SectionTitle>
            <OptionGroup>
              {positionOptions.map(({ label, value }) => (
                <OptionButton
                  key={value}
                  type="button"
                  selected={values.position === value}
                  onClick={() => setFieldValue("position", value)}
                  disabled={isLoading}
                >
                  {label}
                </OptionButton>
              ))}
            </OptionGroup>
          </Section>

          <Section>
            <SectionTitle>Clothing level</SectionTitle>
            <OptionGroup>
              {clothingLevelOptions.map(({ label, value }) => (
                <OptionButton
                  key={value}
                  type="button"
                  selected={values.clothingLevel === value}
                  onClick={() => setFieldValue("clothingLevel", value)}
                  disabled={isLoading}
                >
                  {label}
                </OptionButton>
              ))}
            </OptionGroup>
          </Section>

          <Section>
            <SectionTitle>Background</SectionTitle>
            <OptionGroup>
              {backgroundOptions.map(({ label, value }) => (
                <OptionButton
                  key={value}
                  type="button"
                  selected={values.background === value}
                  onClick={() => setFieldValue("background", value)}
                  disabled={isLoading}
                >
                  {label}
                </OptionButton>
              ))}
            </OptionGroup>
          </Section>
          <GenerateButton type="button" onClick={handleGenerateButtonClick} disabled={isLoading}>
            {isLoading && <Spinner />}
            {!isLoading && (
              <>
                <MagicStickIcon src={magic_stick_icon} />
                Generate Image <RemainingCount>{photoGenerationCost} <TokenIcon src={token_icon} alt="Token" /> </RemainingCount>
              </>
            )}
          </GenerateButton>
        </FormWrapper>
      </Form>
    </Container>
  );
};

export default GenerationStep;

const Container = styled.div<{ isLoading: boolean }>`
  opacity: ${({ isLoading }) => (isLoading ? 0.5 : 1)};
  pointer-events: ${({ isLoading }) => (isLoading ? "none" : "auto")};
  transition: opacity 0.3s ease;
`;

const FormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;

  @media (max-width: 400px) {
    gap: 4px;
  }
`;

const CharacterInfo = styled.div`
  background: #131313;
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
  padding: 4px;
  border-radius: 8px;
  min-height: 98px;

  @media (max-width: 400px) {
    margin-bottom: 4px;
  }
`;

const Section = styled.div`
  margin-bottom: 20px;
`;

const SectionTitle = styled.p`
  font-weight: bold;
  margin-bottom: 10px;
  color: #fff;
`;

const OptionGroup = styled.div`
  display: flex;
  gap: 12px;

  @media (max-width: 470px) {
    flex-wrap: wrap;
  }
`;

const OptionButton = styled.button<{ selected: boolean }>`
  flex: 1;
  font-family: Nunito Sans;
  font-size: 16px;
  font-weight: 400;
  padding: 8px 16px;
  line-height: 18px;
  text-align: center;
  background: ${({ selected }) => (selected ? "linear-gradient(90deg, rgba(158, 94, 238, 0.15) 0%, rgba(183, 89, 234, 0.15) 32%, rgba(198, 93, 233, 0.15) 66%, rgba(184, 133, 244, 0.15) 100%)" : "#131313")};
  border-radius: 32px;
  border: 1px solid ${({ selected }) => (selected ? "#BD8DE7" : "unset")};
  outline: none;

  color: ${({ selected }) => (selected ? "#fff" : "#A9A9A9")};
  cursor: pointer;

  &:hover {
    background: linear-gradient(90deg, rgba(158, 94, 238, 0.15) 0%, rgba(183, 89, 234, 0.15) 32%, rgba(198, 93, 233, 0.15) 66%, rgba(184, 133, 244, 0.15) 100%);
  }

  @media (max-width: 470px) {
    font-size: 14px;
    padding: 6px 12px;
  }

  @media (max-width: 400px) {
    font-size: 12px;
    padding: 4px 8px;
  }
`;

const RemainingCount = styled.span`
  font-family: Nunito Sans;
  font-weight: 700;
  font-size: 12px;
  line-height: 12px;
  align-items: center;
  gap: 4px;
  background: #13131380;
  padding: 4px;
  border-radius: 6px;
  display: flex;
`;

const CharacterImage = styled.img`
  width: 90px;
  height: 90px;
  border-radius: 8px;
  object-fit: cover;

  @media (max-width: 400px) {
    width: 64px;
    height: 64px;
  }
`;

const CharacterName = styled.p`
  font-size: 16px;
  font-weight: bold;
  color: #fff;
  text-align: left;
`;

const GenerateButton = styled.button`
  width: 100%;
  padding: 14px;
  background: linear-gradient(90deg, #9E5EEE 0%, #B759EA 32%, #C65DE9 66%, #B885F4 100%);
  border: none;
  border-radius: 32px;
  color: #fff;
  font-weight: bold;
  cursor: pointer;
  font-size: 16px;
  margin-top: auto;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;

  :disabled {
    background: #818181;
    cursor: not-allowed;
  }
`;

const MagicStickIcon = styled.img`
  width: 16px;
  height: 16px;
`;

const TokenIcon = styled.img`
  width: 16px;
  height: 16px;
  transition: transform 0.5s ease-in-out;

  ${GenerateButton}:hover & {
    transform: rotateY(360deg)
  }
`;
